/**
 * Pure HP damage-delta flash controller (engine chrome).
 * Ghost trail holds the pre-hit ratio, then eases down to the live fill.
 * Heal increases cancel the flash (no reverse white).
 */

export const HP_DAMAGE_FLASH_DEFAULTS = {
  /** Cream/white chunk behind live fill. */
  colorHex: "#f0f0f0",
  color: 0xf0f0f0,
  durationMs: 280,
  enabled: true,
} as const;

export type HpDamageFlashCtrl = {
  prevRatio: number;
  ghostRatio: number;
  from: number;
  to: number;
  startMs: number;
  durationMs: number;
  active: boolean;
};

export type HpDamageFlashStep = {
  live: number;
  ghost: number;
  showGhost: boolean;
};

export function createHpDamageFlashCtrl(
  initialRatio = 1,
): HpDamageFlashCtrl {
  const r = clamp01(initialRatio);
  return {
    prevRatio: r,
    ghostRatio: r,
    from: r,
    to: r,
    startMs: 0,
    durationMs: HP_DAMAGE_FLASH_DEFAULTS.durationMs,
    active: false,
  };
}

export type StepHpDamageFlashOpts = {
  durationMs?: number;
  /** Default true. */
  enabled?: boolean;
};

/**
 * Advance flash state for a new observed fill ratio.
 * Call every frame / HUD sync with monotonic `nowMs`.
 */
export function stepHpDamageFlash(
  ctrl: HpDamageFlashCtrl,
  ratio: number,
  nowMs: number,
  opts?: StepHpDamageFlashOpts,
): HpDamageFlashStep {
  const r = clamp01(ratio);
  const enabled = opts?.enabled !== false;
  const durationMs = opts?.durationMs ?? HP_DAMAGE_FLASH_DEFAULTS.durationMs;

  if (ctrl.active) {
    const t = Math.min(1, (nowMs - ctrl.startMs) / Math.max(1, ctrl.durationMs));
    const e = easeOutQuad(t);
    ctrl.ghostRatio = ctrl.from + (ctrl.to - ctrl.from) * e;
    if (t >= 1) {
      ctrl.active = false;
      ctrl.ghostRatio = ctrl.to;
    }
  }

  if (enabled && r < ctrl.prevRatio - 1e-4) {
    const startGhost = ctrl.active
      ? Math.max(ctrl.ghostRatio, ctrl.prevRatio)
      : ctrl.prevRatio;
    ctrl.from = startGhost;
    ctrl.to = r;
    ctrl.startMs = nowMs;
    ctrl.durationMs = durationMs;
    ctrl.active = true;
    ctrl.ghostRatio = startGhost;
  } else if (r > ctrl.prevRatio + 1e-4) {
    ctrl.active = false;
    ctrl.ghostRatio = r;
  }

  ctrl.prevRatio = r;
  const showGhost = ctrl.active && ctrl.ghostRatio > r + 1e-4;
  return {
    live: r,
    ghost: showGhost ? ctrl.ghostRatio : r,
    showGhost,
  };
}

/** Reset after bar swap / respawn so the next drop flashes from a clean baseline. */
export function resetHpDamageFlash(
  ctrl: HpDamageFlashCtrl,
  ratio = 1,
): void {
  const r = clamp01(ratio);
  ctrl.prevRatio = r;
  ctrl.ghostRatio = r;
  ctrl.from = r;
  ctrl.to = r;
  ctrl.startMs = 0;
  ctrl.active = false;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}
